import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRefreshEffect } from "@/context/RefreshContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Boxes, Plus, Trash2, ClipboardCheck, PackageMinus, AlertTriangle, Inbox } from "lucide-react";
import { formatDatum } from "@/lib/constants";

function diffStatus(soll, ist) {
  const s = Number(soll) || 0;
  const i = Number(ist) || 0;
  if (s === i) return "green";
  const fehlt = s - i;
  if (fehlt > 0 && s > 0 && fehlt >= s * 0.15) return "red";
  return "yellow";
}
const STATUS_STYLES = {
  green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  yellow: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  red: "bg-red-500/10 text-red-400 border-red-500/30",
};

export default function Lagerbestand() {
  const { user } = useAuth();
  const dn = user?.dienstnummer || "";
  const canEditSoll = user?.name === "Administration" || ["Director", "Captain", "Lieutenant", "Sergeant II"].includes(user?.rang);
  const [items, setItems] = useState([]);
  const [entnahmen, setEntnahmen] = useState([]);
  const [fehlmeldungen, setFehlmeldungen] = useState([]);
  const [kontrolle, setKontrolle] = useState({ datum_uhrzeit: "", dienstnummer: "" });
  const [kontrollDn, setKontrollDn] = useState(dn);

  const [entForm, setEntForm] = useState({ datum: "", objekt: "", menge: "", dienstnummer: "" });
  const [fehlForm, setFehlForm] = useState({ was_fehlt: "", wie_viel: "", wann_bemerkt: "", dienstnummer: "" });

  const loadAll = useCallback(() => {
    api.get("/lager/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/lager/entnahmen").then((r) => setEntnahmen(r.data)).catch(() => {});
    api.get("/lager/fehlmeldungen").then((r) => setFehlmeldungen(r.data)).catch(() => {});
    api.get("/lager/kontrolle").then((r) => setKontrolle(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useRefreshEffect(loadAll);

  // Kontrollliste
  const addItem = async () => {
    try {
      const { data } = await api.post("/lager/items", { name: "", soll: 0, ist: 0 });
      setItems((it) => [...it, data]);
    } catch (e) {
      toast.error("Hinzufügen fehlgeschlagen");
    }
  };
  const setItemField = (id, field, val) =>
    setItems((it) => it.map((x) => (x.id === id ? { ...x, [field]: field === "name" ? val : val } : x)));
  const persistItem = async (item) => {
    try {
      await api.put(`/lager/items/${item.id}`, { name: item.name, soll: Number(item.soll) || 0, ist: Number(item.ist) || 0 });
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
      loadAll();
    }
  };
  const deleteItem = async (id) => {
    setItems((it) => it.filter((x) => x.id !== id));
    try { await api.delete(`/lager/items/${id}`); } catch (e) { loadAll(); }
  };

  // Kontrolle abschließen
  const finishKontrolle = async () => {
    const val = { datum_uhrzeit: new Date().toISOString(), dienstnummer: kontrollDn || dn };
    try {
      const { data } = await api.put("/lager/kontrolle", val);
      setKontrolle(data);
      toast.success("Kontrolle abgeschlossen");
    } catch (e) {
      toast.error("Fehlgeschlagen");
    }
  };

  // Entnahmen
  const addEntnahme = async () => {
    if (!entForm.objekt.trim()) { toast.error("Objekt angeben"); return; }
    try {
      const { data } = await api.post("/lager/entnahmen", { ...entForm, menge: Number(entForm.menge) || 0 });
      setEntnahmen((e) => [data, ...e]);
      setEntForm({ datum: "", objekt: "", menge: "", dienstnummer: "" });
      toast.success("Entnahme erfasst");
    } catch (e) { toast.error("Fehlgeschlagen"); }
  };
  const deleteEntnahme = async (id) => {
    setEntnahmen((e) => e.filter((x) => x.id !== id));
    try { await api.delete(`/lager/entnahmen/${id}`); } catch (e) { loadAll(); }
  };

  // Fehlmeldungen
  const addFehl = async () => {
    if (!fehlForm.was_fehlt.trim()) { toast.error("Objekt angeben"); return; }
    try {
      const { data } = await api.post("/lager/fehlmeldungen", { ...fehlForm, wie_viel: Number(fehlForm.wie_viel) || 0 });
      setFehlmeldungen((f) => [data, ...f]);
      setFehlForm({ was_fehlt: "", wie_viel: "", wann_bemerkt: "", dienstnummer: "" });
      toast.success("Fehlmeldung erfasst");
    } catch (e) { toast.error("Fehlgeschlagen"); }
  };
  const deleteFehl = async (id) => {
    setFehlmeldungen((f) => f.filter((x) => x.id !== id));
    try { await api.delete(`/lager/fehlmeldungen/${id}`); } catch (e) { loadAll(); }
  };

  const inputCls = "bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary h-9";

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · Lager"
        title="Lagerbestand"
        description="Kontrollliste, Entnahmen und Fehlmeldungen des Schrankinventars."
        icon={Boxes}
      />
      <div className="p-8 space-y-8">
        {/* Letzte Kontrolle */}
        <div className="flex items-center justify-between flex-wrap gap-4 border border-zinc-800 rounded-sm bg-[#0C0E12] px-5 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Letzte Kontrolle</div>
            <div className="mt-1 text-sm text-zinc-200 font-mono" data-testid="lager-letzte-kontrolle">
              {kontrolle.datum_uhrzeit ? (
                <>
                  {new Date(kontrolle.datum_uhrzeit).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {kontrolle.dienstnummer && <span className="text-zinc-500"> · {kontrolle.dienstnummer}</span>}
                </>
              ) : <span className="text-zinc-600">Noch keine Kontrolle</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input data-testid="lager-kontroll-dn" value={kontrollDn} onChange={(e) => setKontrollDn(e.target.value)} placeholder="Dienstnummer" className={`${inputCls} w-40`} />
            <Button data-testid="lager-finish-kontrolle" onClick={finishKontrolle} className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2">
              <ClipboardCheck className="h-4 w-4" /> Kontrolle abschließen
            </Button>
          </div>
        </div>

        {/* Kontrollliste */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Kontrollliste</h2>
            <Button data-testid="lager-add-item" onClick={addItem} variant="outline" className="rounded-sm border-zinc-700 gap-2">
              <Plus className="h-4 w-4" /> Eintrag
            </Button>
          </div>
          {items.length === 0 ? (
            <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] py-12 text-center text-zinc-600">
              <div className="flex flex-col items-center gap-2"><Inbox className="h-7 w-7" /><span className="font-mono text-sm">Keine Einträge</span></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {items.map((item) => (
                <div key={item.id} data-testid="lager-item-row" className="flex items-center gap-1.5 border border-zinc-800 rounded-sm bg-[#0C0E12] pl-2 pr-1 py-1">
                  <Input data-testid={`lager-item-name-${item.id}`} value={item.name} onChange={(e) => setItemField(item.id, "name", e.target.value)} onBlur={() => persistItem(item)} placeholder="Artikel…" className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary h-8 text-sm flex-1 min-w-0 px-2" />
                  <Input data-testid={`lager-item-soll-${item.id}`} type="number" disabled={!canEditSoll} value={item.soll} onChange={(e) => setItemField(item.id, "soll", e.target.value)} onBlur={() => persistItem(item)} title={canEditSoll ? "Soll" : "Soll (nur Sergeant II und höher)"} placeholder="Soll" className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary h-8 text-sm w-14 px-1.5 text-center disabled:opacity-50 disabled:cursor-not-allowed" />
                  <Input data-testid={`lager-item-ist-${item.id}`} type="number" value={item.ist} onChange={(e) => setItemField(item.id, "ist", e.target.value)} onBlur={() => persistItem(item)} title="Ist" placeholder="Ist" className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary h-8 text-sm w-14 px-1.5 text-center" />
                  <span data-testid={`lager-item-diff-${item.id}`} title="Differenz (Ist − Soll)" className={`shrink-0 w-12 h-7 grid place-items-center rounded-sm border font-mono text-xs ${STATUS_STYLES[diffStatus(item.soll, item.ist)]}`}>
                    {(Number(item.ist) || 0) - (Number(item.soll) || 0)}
                  </span>
                  <Button data-testid={`lager-item-delete-${item.id}`} onClick={() => deleteItem(item.id)} size="icon" variant="ghost" className="h-7 w-7 shrink-0 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-600">Felder je Karte: <span className="text-zinc-400">Name · Soll · Ist · Diff</span>.</p>
        </section>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Entnahmen */}
          <section className="space-y-3">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2"><PackageMinus className="h-4 w-4" /> Entnahmen</h2>
            <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] p-3 grid grid-cols-2 gap-2">
              <Input data-testid="ent-datum" type="date" value={entForm.datum} onChange={(e) => setEntForm({ ...entForm, datum: e.target.value })} className={inputCls} />
              <Input data-testid="ent-objekt" value={entForm.objekt} onChange={(e) => setEntForm({ ...entForm, objekt: e.target.value })} placeholder="Entnommen?" className={inputCls} />
              <Input data-testid="ent-menge" type="number" value={entForm.menge} onChange={(e) => setEntForm({ ...entForm, menge: e.target.value })} placeholder="Menge" className={inputCls} />
              <Input data-testid="ent-dn" value={entForm.dienstnummer} onChange={(e) => setEntForm({ ...entForm, dienstnummer: e.target.value })} placeholder="Dienstnummer" className={inputCls} />
              <Button data-testid="ent-add" onClick={addEntnahme} className="col-span-2 rounded-sm bg-primary hover:bg-blue-600 text-white gap-2 h-9"><Plus className="h-4 w-4" /> Entnahme erfassen</Button>
            </div>
            <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 h-10">Datum</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Objekt</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 w-16">Menge</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Dienstnr.</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entnahmen.length === 0 ? (
                    <TableRow className="border-zinc-800 hover:bg-transparent"><TableCell colSpan={5} className="text-center py-8 text-zinc-600 font-mono text-sm">Keine Entnahmen</TableCell></TableRow>
                  ) : entnahmen.map((e) => (
                    <TableRow key={e.id} data-testid="ent-row" className="border-zinc-800 hover:bg-zinc-900/50">
                      <TableCell className="font-mono text-sm text-zinc-300">{e.datum ? formatDatum(e.datum) : "—"}</TableCell>
                      <TableCell className="text-sm text-zinc-200">{e.objekt || "—"}</TableCell>
                      <TableCell className="font-mono text-sm text-zinc-300">{e.menge}</TableCell>
                      <TableCell className="font-mono text-sm text-zinc-300">{e.dienstnummer || "—"}</TableCell>
                      <TableCell><Button data-testid={`ent-delete-${e.id}`} onClick={() => deleteEntnahme(e.id)} size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          {/* Fehlmeldungen */}
          <section className="space-y-3">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Fehlmeldungen</h2>
            <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] p-3 grid grid-cols-2 gap-2">
              <Input data-testid="fehl-objekt" value={fehlForm.was_fehlt} onChange={(e) => setFehlForm({ ...fehlForm, was_fehlt: e.target.value })} placeholder="Was fehlt?" className={inputCls} />
              <Input data-testid="fehl-wieviel" type="number" value={fehlForm.wie_viel} onChange={(e) => setFehlForm({ ...fehlForm, wie_viel: e.target.value })} placeholder="Wie viel" className={inputCls} />
              <Input data-testid="fehl-wann" type="date" value={fehlForm.wann_bemerkt} onChange={(e) => setFehlForm({ ...fehlForm, wann_bemerkt: e.target.value })} className={inputCls} />
              <Input data-testid="fehl-dn" value={fehlForm.dienstnummer} onChange={(e) => setFehlForm({ ...fehlForm, dienstnummer: e.target.value })} placeholder="Dienstnummer" className={inputCls} />
              <Button data-testid="fehl-add" onClick={addFehl} className="col-span-2 rounded-sm bg-primary hover:bg-blue-600 text-white gap-2 h-9"><Plus className="h-4 w-4" /> Fehlmeldung erfassen</Button>
            </div>
            <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 h-10">Was fehlt?</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 w-16">Wie viel</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Wann bemerkt</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Dienstnr.</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fehlmeldungen.length === 0 ? (
                    <TableRow className="border-zinc-800 hover:bg-transparent"><TableCell colSpan={5} className="text-center py-8 text-zinc-600 font-mono text-sm">Keine Fehlmeldungen</TableCell></TableRow>
                  ) : fehlmeldungen.map((f) => (
                    <TableRow key={f.id} data-testid="fehl-row" className="border-zinc-800 hover:bg-zinc-900/50">
                      <TableCell className="text-sm text-zinc-200">{f.was_fehlt || "—"}</TableCell>
                      <TableCell className="font-mono text-sm text-zinc-300">{f.wie_viel}</TableCell>
                      <TableCell className="font-mono text-sm text-zinc-300">{f.wann_bemerkt ? formatDatum(f.wann_bemerkt) : "—"}</TableCell>
                      <TableCell className="font-mono text-sm text-zinc-300">{f.dienstnummer || "—"}</TableCell>
                      <TableCell><Button data-testid={`fehl-delete-${f.id}`} onClick={() => deleteFehl(f.id)} size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
